import { oc } from "@orpc/contract";
import { z } from "zod";
import {
  EdiGuidelineInterpretationSchema,
  EdiVendorKeySchema,
} from "@erp/shared-types";

/**
 * Vendor EDI implementation guidelines.
 *
 * Two words are used precisely here and mean different things. A **guideline**
 * is the vendor's document — immutable, replaced rather than edited. A
 * **transcription** is our Markdown rendering of that document. The file is the
 * authoritative record; the transcription is only how we read it.
 *
 * Bytes never pass through a handler. `getUploadUrl` mints a presigned staging
 * URL, the browser PUTs the file straight to blob storage, and `confirmUpload`
 * claims the staged blob — which is also where transcription happens, because
 * that is the first request on the server that can see the document at all.
 */

export const EdiGuidelineStatusSchema = z.enum([
  "draft",
  "active",
  "superseded",
]);
export type EdiGuidelineStatus = z.infer<typeof EdiGuidelineStatusSchema>;

/**
 * Step 1 of the presigned upload flow. `contentType` is checked against the
 * spreadsheet/PDF allowlist here, before a byte moves — a `.docx` is refused
 * at the point the user picks it, not after they have waited for the upload.
 */
export const GetEdiGuidelineUploadUrlInputSchema = z.object({
  fileName: z.string().min(1).max(255),
  contentType: z.string().min(1),
  fileSizeBytes: z.number().int().positive(),
});

export type GetEdiGuidelineUploadUrlInput = z.infer<
  typeof GetEdiGuidelineUploadUrlInputSchema
>;

export const GetEdiGuidelineUploadUrlResponseSchema = z.object({
  /** Opaque staging key, handed back verbatim to `confirmUpload`. */
  uploadKey: z.string(),
  uploadUrl: z.string().url(),
});

export const ConfirmEdiGuidelineUploadInputSchema = z.object({
  /** The staging key `getUploadUrl` answered with, its blob now uploaded. */
  uploadKey: z.string().min(1),
  fileName: z.string().min(1).max(255),
  /** As the vendor names it: '845', '849'. */
  transactionSet: z.string().min(1).max(10),
  /** The vendor's own revision label, if their document carries one. */
  vendorRevision: z.string().max(255).optional(),
});

export type ConfirmEdiGuidelineUploadInput = z.infer<
  typeof ConfirmEdiGuidelineUploadInputSchema
>;

/**
 * One row of the guidelines list. Deliberately without the transcription: the
 * Markdown runs tens of kilobytes per document and the list is a list.
 */
export const EdiGuidelineSummarySchema = z.object({
  id: z.string().uuid(),
  transactionSet: z.string(),
  status: EdiGuidelineStatusSchema,
  companyId: z.string().uuid().nullable(),
  companyName: z.string().nullable(),
  /**
   * The vendor keys the bound company declares — the keys an inbound document
   * has to arrive under for this guideline to apply to it. Empty when the
   * guideline is unbound, and empty when the vendor has simply not been
   * declared yet, which is the state in which an active guideline applies to
   * nothing at all.
   */
  companyVendorKeys: z.array(z.string()),
  vendorRevision: z.string().nullable(),
  fileName: z.string(),
  mimeType: z.string(),
  fileSizeBytes: z.number().int().nullable(),
  /**
   * Whether a transcription exists. False means the file is held but could not
   * be read — the state the list surfaces as needing attention.
   */
  hasTranscription: z.boolean(),
  transcriptionError: z.string().nullable(),
  /**
   * Who last corrected our Markdown rendering by hand, and when — null where it
   * is still the converter's own output.
   *
   * Separate from `uploadedByName`, which says who produced the *document*: a
   * corrected transcription is a person's reading, and that is worth knowing
   * when the Markdown and the original file disagree.
   */
  transcriptionEditedByName: z.string().nullable(),
  transcriptionEditedAt: z.coerce.date().nullable(),
  /**
   * Whether this guideline carries a structured interpretation profile — the
   * part of it that changes how the vendor's documents are read. False for a
   * guideline held and readable but not yet profiled, which the list surfaces
   * separately from one that could not be transcribed at all.
   */
  hasInterpretation: z.boolean(),
  uploadedByUserId: z.string().uuid(),
  uploadedByName: z.string().nullable(),
  createdAt: z.coerce.date(),
});
export type EdiGuidelineSummary = z.infer<typeof EdiGuidelineSummarySchema>;

export const EdiGuidelineDetailSchema = EdiGuidelineSummarySchema.extend({
  /** Our Markdown rendering. `""` when the conversion produced nothing. */
  transcription: z.string(),
  /**
   * The structured semantics read off this vendor's document — what their codes
   * mean where vendors legitimately differ. `{}` for a guideline nobody has
   * profiled yet, which reads exactly as the bare standard does.
   */
  interpretation: EdiGuidelineInterpretationSchema,
});
export type EdiGuidelineDetail = z.infer<typeof EdiGuidelineDetailSchema>;

export const ListEdiGuidelinesResponseSchema = z.object({
  guidelines: z.array(EdiGuidelineSummarySchema),
});

export const GetEdiGuidelineInputSchema = z.object({
  id: z.string().uuid(),
});

/**
 * Binding a guideline to the vendor it came from.
 *
 * Deliberately a separate step from uploading: receiving a vendor's document
 * must never be blocked on their company row existing yet, so a guideline can
 * sit unbound indefinitely and still be readable and searchable.
 *
 * `companyId` is nullable so a wrong choice can be taken back rather than only
 * overwritten — an unbound guideline is an honest state, a wrongly bound one
 * is not.
 */
export const BindEdiGuidelineInputSchema = z
  .object({
    id: z.string().uuid(),
    companyId: z.string().uuid().nullable(),
    /**
     * The keys the bound company answers to on inbound EDI, replacing whatever
     * it declared before. Omitted leaves them alone, which is what an unbind
     * does — the keys belong to the company, not to this guideline, and a
     * guideline going away is not a statement about the vendor.
     *
     * Declared here because binding is where a person says which vendor sent a
     * document, and "what that vendor's traffic is called" is the same question
     * asked one level down. Without it a bound, activated guideline applies to
     * nothing: the resolver recognises a vendor only by a declared key.
     */
    vendorKeys: z.array(EdiVendorKeySchema).max(20).optional(),
  })
  // Keys are declared on a vendor, so there is no vendor to declare them on
  // when unbinding. Refused rather than ignored: the service skips them in
  // that case, so the request would otherwise succeed while discarding what
  // the caller sent, and a caller cannot tell a silent discard from a write.
  .refine(
    (input) => input.companyId !== null || input.vendorKeys === undefined,
    {
      message:
        "Vendor keys belong to a vendor: unbind without them, or name the vendor they belong to.",
      path: ["vendorKeys"],
    },
  );

export type BindEdiGuidelineInput = z.infer<typeof BindEdiGuidelineInputSchema>;

/**
 * Putting a guideline in force.
 *
 * Only the id: which guideline supersedes which is derived from the vendor and
 * transaction set the guideline already carries, never passed in. A caller
 * naming the row to demote could demote one it never read.
 */
export const ActivateEdiGuidelineInputSchema = z.object({
  id: z.string().uuid(),
});

export type ActivateEdiGuidelineInput = z.infer<
  typeof ActivateEdiGuidelineInputSchema
>;

/**
 * Recording what a vendor's guideline says their codes mean.
 *
 * Editable while the guideline is **active**, unlike its transcription: the
 * transcription is a rendering of an immutable document and freezes when the
 * document becomes authoritative, whereas a profile is read *off* that document
 * by a person, is normally filled in after activation, and is corrected as
 * questions are answered — `PAD02 = SS` is one such open question today.
 *
 * The whole profile is replaced, not merged. A profile is small and is edited
 * as a whole in one dialog, and merging would leave no way to *remove* a code
 * someone recorded by mistake — which, for a map whose absences are load
 * bearing, is the operation that matters most.
 */
export const UpdateEdiGuidelineInterpretationInputSchema = z.object({
  id: z.string().uuid(),
  interpretation: EdiGuidelineInterpretationSchema,
});

export type UpdateEdiGuidelineInterpretationInput = z.infer<
  typeof UpdateEdiGuidelineInterpretationInputSchema
>;

/**
 * Correcting our Markdown rendering of a vendor's document.
 *
 * Only the transcription, and only while the guideline is a draft. The vendor's
 * file is never touched — correcting a transcription fixes how we read an
 * unchanged source, where a new document is a new guideline. A guideline in
 * force is frozen: it is what was read when someone put it in force, and the
 * way to correct it is to upload the document again.
 *
 * An empty transcription is allowed: a reader who cannot make sense of a
 * mangled conversion may honestly leave nothing rather than invent a reading,
 * and the list already surfaces an empty transcription as needing attention.
 */
export const UpdateEdiGuidelineTranscriptionInputSchema = z.object({
  id: z.string().uuid(),
  transcription: z.string().max(2_000_000),
});

export type UpdateEdiGuidelineTranscriptionInput = z.infer<
  typeof UpdateEdiGuidelineTranscriptionInputSchema
>;

/**
 * Running the conversion again over the file already held.
 *
 * The converter improves, and a document that produced nothing on upload may
 * convert on a later attempt. Nothing is re-uploaded and nothing is re-fetched
 * from the vendor: this reads the same stored bytes and replaces the Markdown
 * with what they produce now, which is why it is refused on a guideline in
 * force for the same reason a hand correction is.
 */
export const RetranscribeEdiGuidelineInputSchema = z.object({
  id: z.string().uuid(),
});

/**
 * The keys one company declares, read before they are edited.
 *
 * Its own, not the ones on the guideline being bound: binding can move a
 * guideline from one vendor to another, and the keys belong to whichever
 * vendor is chosen. Submitting the previous vendor's list onto the new one
 * would overwrite a declaration nobody saw — the same silent mis-resolution
 * this feature exists to remove.
 */
export const GetEdiCompanyVendorKeysInputSchema = z.object({
  companyId: z.string().uuid(),
});

export const GetEdiCompanyVendorKeysResponseSchema = z.object({
  vendorKeys: z.array(z.string()),
});

export const GetEdiGuidelineDocumentUrlResponseSchema = z.object({
  url: z.string(),
  fileName: z.string(),
});

export const ediGuidelineContracts = {
  getUploadUrl: oc
    .input(GetEdiGuidelineUploadUrlInputSchema)
    .output(GetEdiGuidelineUploadUrlResponseSchema)
    .route({
      method: "POST",
      summary: "Get upload URL for an EDI guideline",
      description:
        "Checks the file is a spreadsheet or PDF within the size limit, then returns a one-hour presigned staging URL and upload key for the browser to upload to.",
    }),
  confirmUpload: oc
    .input(ConfirmEdiGuidelineUploadInputSchema)
    .output(EdiGuidelineDetailSchema)
    .route({
      method: "POST",
      summary: "Save an uploaded EDI guideline",
      description:
        "Claims the staged file by upload key, verifies its real type and size, transcribes it to Markdown, and creates a draft guideline for the given transaction set.",
    }),
  list: oc.output(ListEdiGuidelinesResponseSchema).route({
    method: "GET",
    summary: "List vendor EDI guidelines",
    description:
      "Returns every EDI guideline the organization holds, with status, transaction set, bound vendor and its keys, uploader, and transcription or profile health flags; no transcription text.",
  }),
  get: oc
    .input(GetEdiGuidelineInputSchema)
    .output(EdiGuidelineDetailSchema)
    .route({
      method: "GET",
      summary: "Get an EDI guideline",
      description:
        "Returns one guideline by id, including its full Markdown transcription and the interpretation profile recorded for the vendor's codes.",
    }),
  bind: oc
    .input(BindEdiGuidelineInputSchema)
    .output(EdiGuidelineDetailSchema)
    .route({
      method: "POST",
      summary: "Bind an EDI guideline to a vendor",
      description:
        "Links a guideline to the vendor company that sent it, optionally replacing that company's inbound vendor keys; a null company unbinds it.",
    }),
  activate: oc
    .input(ActivateEdiGuidelineInputSchema)
    .output(EdiGuidelineDetailSchema)
    .route({
      method: "POST",
      summary: "Activate an EDI guideline",
      description:
        "Puts a bound guideline in force, superseding the vendor's previously active guideline for the same transaction set. Fails if unbound or another activation won.",
    }),
  updateInterpretation: oc
    .input(UpdateEdiGuidelineInterpretationInputSchema)
    .output(EdiGuidelineDetailSchema)
    .route({
      method: "POST",
      summary: "Update an EDI guideline's code interpretation",
      description:
        "Replaces the whole interpretation profile recording what the vendor's codes mean, which changes how their inbound documents are read. Allowed on active guidelines.",
    }),
  updateTranscription: oc
    .input(UpdateEdiGuidelineTranscriptionInputSchema)
    .output(EdiGuidelineDetailSchema)
    .route({
      method: "POST",
      summary: "Edit an EDI guideline's transcription",
      description:
        "Replaces the Markdown transcription of a draft guideline with a hand correction, records who edited it, and clears any conversion error. Refused once active.",
    }),
  retranscribe: oc
    .input(RetranscribeEdiGuidelineInputSchema)
    .output(EdiGuidelineDetailSchema)
    .route({
      method: "POST",
      summary: "Retranscribe an EDI guideline",
      description:
        "Reruns Markdown conversion over the stored guideline file and replaces a draft's transcription, discarding earlier hand edits. Refused for a guideline in force.",
    }),
  getCompanyVendorKeys: oc
    .input(GetEdiCompanyVendorKeysInputSchema)
    .output(GetEdiCompanyVendorKeysResponseSchema)
    .route({
      method: "GET",
      summary: "Get a vendor's inbound EDI keys",
      description:
        "Returns the vendor keys a company declares, which inbound EDI documents must arrive under for that vendor's guideline to apply.",
    }),
  getDocumentUrl: oc
    .input(GetEdiGuidelineInputSchema)
    .output(GetEdiGuidelineDocumentUrlResponseSchema)
    .route({
      method: "GET",
      summary: "Download an EDI guideline's original file",
      description:
        "Returns a five-minute signed URL and file name for the vendor's original guideline document.",
    }),
};
